export const EDUCATOR_ROLE_OPTIONS = [
  { value: 'shadow-teacher', label: 'Shadow Teacher' },
  { value: 'class-teacher', label: 'Class Teacher' },
  { value: 'special-educator', label: 'Special Educator' },
  { value: 'school-counselor', label: 'School Counselor' },
  { value: 'sport-coach', label: 'Sport Coach' },
  { value: 'visual-arts-teacher', label: 'Visual Arts Teacher' },
  { value: 'performing-arts-teacher', label: 'Music / Drama Teacher' },
  { value: 'academic-tutor', label: 'Academic Tutor' },
  { value: 'activity-coach', label: 'Activity Coach' },
  { value: 'therapy-skills-coach', label: 'Therapist or Skills Coach' },
];

export const EDUCATOR_ROLE_DETAILS = [
  {
    value: 'shadow-teacher',
    shortName: 'Shadow Teacher',
    icon: '🧩',
    bullets: [
      '1:1 classroom support',
      'Focus strategies',
      'How to step in vs step back',
    ],
  },
  {
    value: 'class-teacher',
    shortName: 'Class Teacher',
    icon: '📚',
    bullets: [
      'Classroom setup',
      'What adjustments help',
      'How to communicate with parents',
    ],
  },
  {
    value: 'special-educator',
    shortName: 'Special Educator',
    icon: '📝',
    bullets: ['Learning profile', 'Skill gaps', "What's worked in therapy"],
  },
  {
    value: 'school-counselor',
    shortName: 'School Counselor',
    icon: '💛',
    bullets: ['Emotional triggers', 'Calming strategies', 'Social dynamics'],
  },
  {
    value: 'sport-coach',
    shortName: 'Sports Coach',
    icon: '🏃',
    bullets: [
      'Movement and focus',
      'What motivates them',
      'How to coach without friction',
    ],
  },
  {
    value: 'visual-arts-teacher',
    shortName: 'Arts Teacher',
    icon: '🎨',
    bullets: [
      'Creative strengths',
      'Sensory needs',
      'How they express themselves',
    ],
  },
  {
    value: 'performing-arts-teacher',
    shortName: 'Music / Drama Teacher',
    icon: '🎭',
    bullets: [
      'Attention in group settings',
      'Performance anxiety',
      'Participation style',
    ],
  },
  {
    value: 'academic-tutor',
    shortName: 'Tutor',
    icon: '✏️',
    bullets: [
      'How they learn best',
      'What frustrates them',
      'Homework strategies',
    ],
  },
  {
    value: 'activity-coach',
    shortName: 'Activity Coach',
    icon: '🌟',
    bullets: [
      'Engagement patterns',
      'What builds confidence',
      'Social dynamics',
    ],
  },
  {
    value: 'therapy-skills-coach',
    shortName: 'Therapist or Skills Coach',
    icon: '🛠️',
    bullets: [
      'What the parent has observed',
      'Strategies already tried',
      'Goals',
    ],
  },
];

export const EDUCATOR_PURCHASE_CONTENT = {
  'shadow-teacher': {
    badge: 'For 1:1 classroom support',
    title: 'Give your child’s shadow teacher a calm, practical starting point.',
    intro:
      'This handbook translates what you already know into guidance a shadow teacher can use from the very first school day.',
    summary:
      'Best for close daily support inside the classroom, transitions, prompting, and building independence over time.',
    problem:
      'Without context, a shadow teacher can end up reacting moment by moment instead of supporting with consistency. This handbook helps them begin with the child, not just the behaviour.',
    outcomes: [
      'How to step in without over-helping',
      'What keeps the child regulated during a full school day',
      'How to support focus, transitions, and growing independence',
    ],
    valueProps: [
      'Written for one adult supporting one child closely',
      'Turns family insight into practical, school-day guidance',
      'Helps support feel steadier, calmer, and less reactive',
    ],
  },
  'class-teacher': {
    badge: 'For the main classroom teacher',
    title:
      'Help the class teacher understand what works before small issues grow.',
    intro:
      'This version is written for the teacher managing the whole classroom while still needing clear, usable guidance for your child.',
    summary:
      'Best for mainstream classroom settings, school routines, communication, and day-to-day adjustments.',
    problem:
      'Class teachers are balancing many children at once. The clearer your child’s patterns are from the start, the easier it is for helpful adjustments to happen early.',
    outcomes: [
      'What classroom conditions help the child settle and learn',
      'How to give instructions, transitions, and feedback more effectively',
      'What to notice early before stress or dysregulation builds',
    ],
    valueProps: [
      'Designed for busy classroom realities',
      'Makes helpful adjustments easier to apply quickly',
      'Supports stronger school-home understanding without long explanation loops',
    ],
  },
  'special-educator': {
    badge: 'For targeted learning support',
    title: 'Give the special educator a fuller picture before sessions begin.',
    intro:
      'This handbook helps a special educator start from the child’s real learning profile instead of rebuilding context from scratch.',
    summary:
      'Best for remediation, learning support, skill-building plans, and structured school or centre-based interventions.',
    problem:
      'Too much support time is lost gathering scattered background. This handbook gives the special educator a clearer starting point before intervention planning begins.',
    outcomes: [
      'How the child learns best in structured sessions',
      'What strengths and gaps show up across tasks',
      'Which supports already help and which tend to backfire',
    ],
    valueProps: [
      'Useful for both school-based and centre-based support',
      'Captures parent observations that often get missed in formal paperwork',
      'Helps sessions start with more precision and less guesswork',
    ],
  },
  'school-counselor': {
    badge: 'For emotional and social support',
    title:
      'Help the school counselor meet the child with context, not assumptions.',
    intro:
      'This version is tuned for emotional regulation, school-based wellbeing, and the social patterns a counselor needs to understand quickly.',
    summary:
      'Best for counselling rooms, pastoral care, emotional check-ins, and school-home support planning.',
    problem:
      'When a counselor only sees isolated incidents, it is harder to understand what is really driving the child’s stress. This handbook adds context around triggers, safety, and repair.',
    outcomes: [
      'What situations tend to trigger shutdown, overwhelm, or conflict',
      'How the child usually re-regulates and reconnects',
      'What kind of adult response feels safe and effective',
    ],
    valueProps: [
      'Focused on emotional context, not just incidents',
      'Supports calmer, more attuned adult responses',
      'Useful for both regular check-ins and difficult school periods',
    ],
  },
  'sport-coach': {
    badge: 'For sport and movement settings',
    title:
      'Give the coach guidance they can use during practice, not just after it.',
    intro:
      'This handbook helps coaches understand focus, motivation, and regulation in active environments where things move fast.',
    summary:
      'Best for sports teams, PE-style coaching, drills, games, and movement-heavy group environments.',
    problem:
      'In fast-moving sessions, a coach often has seconds to decide how to redirect, encourage, or adjust. This handbook makes those moments easier to handle well.',
    outcomes: [
      'What helps the child stay engaged during drills and transitions',
      'How to correct or redirect without losing confidence',
      'What motivates effort, teamwork, and follow-through',
    ],
    valueProps: [
      'Built for real-time coaching situations',
      'Balances confidence-building with useful structure',
      'Works for both individual coaching and group training',
    ],
  },
  'visual-arts-teacher': {
    badge: 'For art and visual expression',
    title:
      'Help the arts teacher support creativity without missing the child’s needs.',
    intro:
      'This version gives visual arts teachers the practical context they need around attention, sensory preferences, and participation style.',
    summary:
      'Best for art rooms, making-based classes, and creative environments with open-ended tasks.',
    problem:
      'Creative spaces can be wonderful, but they can also be overwhelming or hard to enter. This handbook helps the teacher understand how the child participates best.',
    outcomes: [
      'How the child approaches creative tasks and materials',
      'What sensory or setup factors affect participation',
      'How to encourage expression without adding pressure',
    ],
    valueProps: [
      'Sensitive to sensory and participation differences',
      'Supports open-ended work without losing structure',
      'Helps creative strengths show up more easily',
    ],
  },
  'performing-arts-teacher': {
    badge: 'For music, drama, and performance settings',
    title:
      'Give performance teachers clearer guidance for attention, confidence, and participation.',
    intro:
      'This handbook is shaped for music, drama, and other performance-led settings where focus, anxiety, and group rhythm all matter.',
    summary:
      'Best for rehearsals, group classes, performances, and expressive small-batch learning environments.',
    problem:
      'Performance settings can quickly expose anxiety, hesitation, or overload. This handbook helps the teacher understand what supports participation without adding pressure.',
    outcomes: [
      'How the child joins, hesitates, or holds back in group performance settings',
      'What helps with attention, transitions, and performance nerves',
      'How to keep lessons encouraging while still structured',
    ],
    valueProps: [
      'Written for expressive, high-attention environments',
      'Helps participation feel safer and more manageable',
      'Useful in both private lessons and small-group formats',
    ],
  },
  'academic-tutor': {
    badge: 'For tutoring and homework support',
    title:
      'Help the tutor spend less time figuring things out and more time teaching well.',
    intro:
      'This version is written for focused academic support outside school, where the child’s learning style needs to be understood quickly.',
    summary:
      'Best for private tutoring, small-batch academic help, homework sessions, and study-skill support.',
    problem:
      'Tutoring works best when the adult already knows how the child responds to challenge, correction, and frustration. This handbook shortens that learning curve.',
    outcomes: [
      'How the child best takes in explanations and correction',
      'What leads to frustration, avoidance, or shutdown during study',
      'Which strategies help work actually get completed',
    ],
    valueProps: [
      'Especially useful for 1:1 and small-batch tutoring',
      'Brings clarity to homework struggles and study habits',
      'Helps sessions focus on learning, not repeated friction',
    ],
  },
  'activity-coach': {
    badge: 'For clubs, enrichment, and small-group coaching',
    title:
      'Give the activity coach enough context to build confidence from the start.',
    intro:
      'This handbook helps activity leaders understand participation, confidence, and regulation in less formal but still demanding settings.',
    summary:
      'Best for enrichment classes, hobby groups, clubs, and coaches working with children in smaller batches.',
    problem:
      'In enrichment settings, children are often expected to join in quickly. This handbook helps the coach understand what supports participation, confidence, and follow-through.',
    outcomes: [
      'What draws the child in and keeps them engaged',
      'How they respond to group dynamics and feedback',
      'What support helps them participate without pressure',
    ],
    valueProps: [
      'Tailored to confidence and participation in group settings',
      'Useful across clubs, enrichment, and skills-based classes',
      'Helps the adult respond with more understanding from the start',
    ],
  },
  'therapy-skills-coach': {
    badge: 'For therapy and skills-based support',
    title:
      'Help the therapist or skills coach begin with the parent’s clearest observations.',
    intro:
      'This version supports specialists who are helping the child build concrete regulation, communication, or daily living skills.',
    summary:
      'Best for therapy, executive-function coaching, social skills work, and other one-to-one developmental support.',
    problem:
      'Therapy works better when a specialist can begin with the family’s lived picture, not only intake fragments. This handbook organizes that picture into something easier to act on.',
    outcomes: [
      'What the parent is already noticing at home and school',
      'Which strategies have been tried and how the child responded',
      'What goals feel most important right now',
    ],
    valueProps: [
      'Bridges home observations and specialist support',
      'Useful for one-to-one developmental or therapeutic work',
      'Helps goals and strategies feel more aligned from the start',
    ],
  },
};

export const LOCKED_CHILD_FIELDS = [
  'childName',
  'grade',
  'schoolName',
  'academicYear',
  'pronouns',
  'ageBand',
];

export const SHARED_CHILD_FIELDS = ['condition', 'additionalProfiles'];

export const REMOVED_QUESTION_FIELDS = ['outputMode'];

export const SHARED_CHILD_BASELINE_FIELDS = [
  ...LOCKED_CHILD_FIELDS,
  ...SHARED_CHILD_FIELDS,
];

const LEGACY_ROLE_MAP = {
  'Shadow Teacher (Discreet 1-on-1 support and help)': 'shadow-teacher',
  'Class Teacher (Classroom management & seat adjustments)': 'class-teacher',
  'Tuition Teacher (Private tutoring & homework help)': 'special-educator',
  'Sports Coach (Physical training & games on the field)': 'sport-coach',
  'Art/Music Teacher (Creative tasks, instruments & sensory tools)':
    'school-counselor',
};

const CURRENT_ROLE_LABEL_MAP = EDUCATOR_ROLE_OPTIONS.reduce((map, option) => {
  map[option.label] = option.value;
  return map;
}, {});

export function getEducatorRoleLabel(value) {
  return (
    EDUCATOR_ROLE_OPTIONS.find((option) => option.value === value)?.label ??
    'Shadow Teacher'
  );
}

export function getEducatorRoleDetails(value) {
  return (
    EDUCATOR_ROLE_DETAILS.find((option) => option.value === value) ??
    EDUCATOR_ROLE_DETAILS[0]
  );
}

export function getEducatorPurchaseContent(value) {
  return (
    EDUCATOR_PURCHASE_CONTENT[value] ??
    EDUCATOR_PURCHASE_CONTENT['shadow-teacher']
  );
}

export function normalizeEducatorRole(value) {
  return (
    LEGACY_ROLE_MAP[value] ||
    CURRENT_ROLE_LABEL_MAP[value] ||
    value ||
    'shadow-teacher'
  );
}

export function createChildId(childName) {
  return String(childName || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .slice(0, 50);
}

export function createProductId(childId, educatorRole, legacyProfileId = '') {
  return legacyProfileId || `${childId}--${educatorRole}`;
}

function pickAnswerFields(answers, fields) {
  return Object.fromEntries(
    Object.entries(answers || {}).filter(
      ([key, value]) =>
        fields.includes(key) &&
        value !== undefined &&
        value !== null &&
        !(Array.isArray(value) && value.length === 0) &&
        value !== '',
    ),
  );
}

export function stripRemovedAnswerFields(answers) {
  return Object.fromEntries(
    Object.entries(answers || {}).filter(
      ([key]) => !REMOVED_QUESTION_FIELDS.includes(key),
    ),
  );
}

export function isLockedChildField(fieldId) {
  return LOCKED_CHILD_FIELDS.includes(fieldId);
}

export function isSharedChildField(fieldId) {
  return SHARED_CHILD_FIELDS.includes(fieldId);
}

export function splitChildBaselineAnswers(answers) {
  return pickAnswerFields(
    stripRemovedAnswerFields(answers),
    SHARED_CHILD_BASELINE_FIELDS,
  );
}

export function splitProductAnswers(answers) {
  return Object.fromEntries(
    Object.entries(stripRemovedAnswerFields(answers || {})).filter(
      ([key]) =>
        !SHARED_CHILD_BASELINE_FIELDS.includes(key) &&
        key !== 'educatorRole' &&
        key !== 'role',
    ),
  );
}

export function mergeProductAnswers(
  childAnswers,
  productAnswers,
  educatorRole,
) {
  return {
    ...splitChildBaselineAnswers(childAnswers),
    ...splitProductAnswers(productAnswers),
    educatorRole,
  };
}
